import { FolderItem as FolderItemType } from '../../types'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import { VirtualItem } from '@tanstack/react-virtual'

type Props = {
  folder: FolderItemType
  virtualItem: VirtualItem
  selectMultipleFiles: boolean
  addFileIdToSelectedFileRef: (fileId: string) => void
  fileHasChecked: (fileId: string) => boolean
  showRenamePopup: (fileId: string) => void
  deleteFolder?: (folderItem: FolderItemType) => void
  movable: boolean
  move?: ({
    id,
    name,
    type,
  }: {
    id: string
    name: string
    type: 'folder' | 'file'
  }) => void
  onFolderClick?: (
    folder: FolderItemType,
    setFolderStack: ((stack: FolderItemType[]) => void) | undefined
  ) => void
  setFolderStack?: (stack: FolderItemType[]) => void
  lastItem: boolean
  firstItem: boolean
  totalItems: number
}

const FolderItem = ({
  folder,
  virtualItem,
  selectMultipleFiles,
  addFileIdToSelectedFileRef,
  fileHasChecked,
  showRenamePopup,
  deleteFolder,
  movable,
  move,
  onFolderClick,
  setFolderStack,
  lastItem,
  totalItems,
}: Props) => {
  return (
    <div
      key={folder.id}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `${virtualItem.size}px`,
        transform: `translateY(${virtualItem.start}px)`,
      }}
    >
      <li className={`rounded-md p-1 hover:bg-gray-100`}>
        <div className="">
          <div className="flex justify-between items-center h-11">
            <div
              className="flex w-full h-full justify-start items-center cursor-pointer"
              onClick={() =>
                onFolderClick ? onFolderClick(folder, setFolderStack) : null
              }
            >
              {selectMultipleFiles && (
                <div className="flex items-center p-2">
                  <input
                    type="checkbox"
                    name="select-all"
                    className="mr-2 accent-[#107C41] border-[#616161] w-4 h-4"
                    onChange={() => addFileIdToSelectedFileRef(folder.id)}
                    checked={fileHasChecked(folder.id)}
                  />
                </div>
              )}
              <div className="flex items-center p-2">
                <FolderOutlinedIcon sx={{ fontSize: 16 }} />
              </div>

              <div className="mr-2">
                <h3 className="text-sm font-medium leading-5">{folder.name}</h3>
              </div>
            </div>
            {!selectMultipleFiles && (
              <div className="flex items-center group/ellipses">
                <div className="relative group/rename hidden group-hover/ellipses:flex group-hover/ellipses:justify-center mr-2 hover:bg-gray-500 rounded">
                  <button
                    className="w-full h-full p-2.5 transition ease-in-out delay-100 hover:scale-110"
                    onClick={() => showRenamePopup(folder.id)}
                  >
                    <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                  </button>
                  <div
                    className={`absolute ${
                      lastItem
                        ? totalItems > 1
                          ? 'bottom-8 before:-bottom-1'
                          : 'bottom-14 before:-bottom-1'
                        : 'top-14 before:-top-1'
                    } scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/rename:scale-100 group-hover/rename:w-max before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-0  before:right-0 before:mx-auto`}
                  >
                    Rename
                  </div>
                </div>
                {movable && move && (
                  <div className="relative group/move hidden group-hover/ellipses:flex group-hover/ellipses:justify-center mr-2 hover:bg-gray-500 rounded">
                    <button
                      className="w-full h-full p-2.5 transition ease-in-out delay-100 hover:scale-110"
                      onClick={() =>
                        move({
                          id: folder.id,
                          name: folder.name,
                          type: 'folder',
                        })
                      }
                    >
                      <DriveFileMoveOutlinedIcon sx={{ fontSize: 20 }} />
                    </button>
                    <div
                      className={`absolute ${
                        lastItem
                          ? totalItems > 1
                            ? 'bottom-8 before:-bottom-1'
                            : 'bottom-14 before:-bottom-1'
                          : 'top-14 before:-top-1'
                      } scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/move:scale-100 group-hover/move:w-max before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-0  before:right-0 before:mx-auto`}
                    >
                      Move
                    </div>
                  </div>
                )}
                <div className="relative group/deletefile hidden group-hover/ellipses:flex group-hover/ellipses:justify-center mr-2 hover:bg-gray-500 rounded">
                  <button
                    className="w-full h-full p-2.5 rounded transition ease-in-out delay-100 hover:scale-110"
                    onClick={() => (deleteFolder ? deleteFolder(folder) : null)}
                  >
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                  </button>
                  <div
                    className={`absolute ${
                      lastItem
                        ? totalItems > 1
                          ? 'bottom-8 before:-bottom-1'
                          : 'bottom-14 before:-bottom-1'
                        : 'top-14 before:-top-1'
                    } scale-0 rounded bg-neutral-700 p-2 text-xs text-white group-hover/deletefile:scale-100 group-hover/deletefile:w-max before:w-2 before:h-2 before:rotate-45 before:bg-neutral-700 before:absolute before:z-[-1]  before:left-0  before:right-0 before:mx-auto`}
                  >
                    Delete
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </li>
    </div>
  )
}

export default FolderItem
